import React from 'react';
import { useParams } from 'react-router-dom';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { SettingsToggle } from '../components/SettingsToggle';
import { SETTINGS_FIELD_LABEL } from '../utils/settingsSectionStyles';

const EMAIL_EVENTS = [
  {
    key: 'mention',
    title: 'Erwähnungen',
    description: 'Wenn dich jemand in einer Notiz oder Aktivität erwähnt.',
  },
  {
    key: 'task_assigned',
    title: 'To-dos',
    description: 'Wenn dir jemand ein To-do zuweist.',
  },
  {
    key: 'inbound_lead',
    title: 'Eingehende Website-Leads',
    description: 'Wenn ein neues Projekt über die externe API ankommt.',
  },
  {
    key: 'work_report_submitted',
    title: 'Arbeitsberichte',
    description: 'Wenn ein Techniker einen Arbeitsbericht einreicht.',
  },
  {
    key: 'payroll_change_request',
    title: 'Zeiterfassungs-Anträge',
    description: 'Wenn jemand eine Änderung an der Zeiterfassung beantragt.',
  },
] as const;

type EmailEventKey = (typeof EMAIL_EVENTS)[number]['key'];

type EmailPrefs = Record<EmailEventKey, boolean>;

const DEFAULT_EMAIL_PREFS: EmailPrefs = {
  mention: true,
  task_assigned: true,
  inbound_lead: true,
  work_report_submitted: true,
  payroll_change_request: true,
};

export function NotificationSettingsSection() {
  const { instanceId = 'default' } = useParams<{ instanceId: string }>();
  const [email, setEmail] = React.useState<EmailPrefs>(DEFAULT_EMAIL_PREFS);
  const [loaded, setLoaded] = React.useState(false);
  const [savingKey, setSavingKey] = React.useState<EmailEventKey | null>(null);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const instanceIdRef = React.useRef(instanceId);
  instanceIdRef.current = instanceId;

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setSaved(false);
    setSavingKey(null);
    setError(null);
    fetch('/api/me/notification-preferences', { credentials: 'include' })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error(
            (d as { error?: string }).error || 'Benachrichtigungseinstellungen konnten nicht geladen werden.',
          );
        }
        return d as { email?: Partial<EmailPrefs> };
      })
      .then((d) => {
        if (cancelled) return;
        setEmail({ ...DEFAULT_EMAIL_PREFS, ...(d.email ?? {}) });
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen.');
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  const save = async (key: EmailEventKey, nextEnabled: boolean) => {
    const saveInstanceId = instanceId;
    const previous = email[key];
    setEmail((cur) => ({ ...cur, [key]: nextEnabled }));
    setSavingKey(key);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/me/notification-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ instanceId: saveInstanceId, email: { [key]: nextEnabled } }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((d as { error?: string }).error || 'Speichern fehlgeschlagen.');
      if (instanceIdRef.current !== saveInstanceId) return;
      setEmail({ ...DEFAULT_EMAIL_PREFS, ...((d as { email?: Partial<EmailPrefs> }).email ?? {}) });
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      if (instanceIdRef.current !== saveInstanceId) return;
      setEmail((cur) => ({ ...cur, [key]: previous }));
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      if (instanceIdRef.current === saveInstanceId) setSavingKey(null);
    }
  };

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      <SettingsCard
        title="E-Mail-Benachrichtigungen"
        description="In der App siehst du alle Hinweise weiterhin. Hier steuerst du nur, wofür du zusätzlich eine E-Mail bekommst."
        actions={<SettingsCardSavedStatus saved={saved} />}
      >
        {error ? (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
        ) : null}

        {!loaded ? (
          <InlineLoadingState label="Einstellungen werden geladen …" className="py-6" />
        ) : (
          <div className="divide-y divide-app-border">
            {EMAIL_EVENTS.map((event) => (
              <div key={event.key} className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className={SETTINGS_FIELD_LABEL}>{event.title}</p>
                  <p className="text-sm text-app-text-muted mt-1">{event.description}</p>
                </div>
                <SettingsToggle
                  active={email[event.key]}
                  disabled={savingKey === event.key}
                  onClick={() => void save(event.key, !email[event.key])}
                  label={event.title}
                />
              </div>
            ))}
          </div>
        )}
      </SettingsCard>
    </div>
  );
}
